import i18next from 'i18next';
import Cookies from 'universal-cookie';
import { supportedLngs } from 'src/i18n';
import { useState, createContext } from 'react';

import type { Doctor, Organization, DoctorProfile } from 'src/types/api';

export interface DoctorContextValue {
  lang: string;
  setLang: (lang?: string) => void;
  doctor: Doctor | undefined;
  setDoctor: (profile?: DoctorProfile) => void;
  currentPatient: string | null;
  setCurrentPatient: (patientId: string | null) => void;
}

const DoctorContext = createContext<DoctorContextValue>({} as DoctorContextValue);

const roleAccess: Record<string, string[]> = {
  cases: ['details', 'invites', 'instructions', 'doctor', 'reviews'],
  common: ['patients', 'details', 'redirect', 'invites', 'instructions'],
  'GP admin': ['gp'],
  'Medical staff - doctor': ['doctor', 'screening'],
  'Medical staff - nurse': ['nurse', 'screening'],
};

type Props = {
  children?: React.ReactNode;
};

export const DoctorProvider = ({ children }: Props) => {
  const [currentPatient, setCurrentPatient] = useState<string | null>(null);
  const [lang, setLang] = useState(i18next.language);
  const [doctor, setDoctor] = useState<Doctor>();
  const cookies = new Cookies();

  const getPermissions = (groups?: string[], org?: Organization, userType?: string) => {
    if (org?.slug === 'podobnik' && userType === 'nurse') return ['invites'];
    if (org?.cases_for_review) {
      const cases = [...roleAccess.cases];
      if (org?.slug === 'valleywise' && userType !== 'doctor') {
        return cases.filter((p) => !['doctor', 'reviews'].includes(p));
      }
      return cases;
    }
    const permissions = [...roleAccess.common];
    groups?.forEach((group) => {
      roleAccess[group]?.forEach((permission) => {
        if (!permissions.includes(permission)) permissions.push(permission);
      });
    });
    return permissions;
  };

  const modifyDoctor = (oldDoctor?: DoctorProfile) => {
    const doula = oldDoctor?.project.slug === 'usa';
    const modifiedDoctor = {
      doula,
      ...oldDoctor,
      displayName: `${oldDoctor?.first_name} ${oldDoctor?.last_name}`,
      access: getPermissions(oldDoctor?.groups, oldDoctor?.organization, oldDoctor?.user_type),
    } as Doctor;

    if (doula) modifiedDoctor.access.push('demo', 'chat');

    // if there was a cached language, don't update with language from doctors profile
    if (!cookies.get('language-cached')) modifyLanguage(modifiedDoctor.language);
    setDoctor(modifiedDoctor);
  };

  const modifyLanguage = (languageInput?: string) => {
    if (languageInput) {
      let language: string | string[] = languageInput.split('-');
      language = language.length > 1 ? `${language[0]}-${language[1].toUpperCase()}` : language[0];

      if (supportedLngs.includes(language)) {
        i18next.changeLanguage(language);
        setLang(language);
      }
    }
  };

  return (
    <DoctorContext.Provider
      value={{
        lang,
        setLang: modifyLanguage,
        doctor,
        setDoctor: modifyDoctor,
        currentPatient,
        setCurrentPatient,
      }}
    >
      {children}
    </DoctorContext.Provider>
  );
};

export default DoctorContext;
